// Drawer para que el profesor cree una clase grupal nueva.

import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Button } from '@/components/atoms/Button';
import { Icon } from '@/components/atoms/Icon';
import { ApiError } from '@/services/api';
import { createGroupBooking } from '@/services/bookings';
import styles from './CreateGroupDrawer.module.scss';

interface CreateGroupDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teacherId: number;
  onCreated: () => void;
}

export function CreateGroupDrawer({ open, onOpenChange, teacherId, onCreated }: CreateGroupDrawerProps) {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('10:00');
  const [duration, setDuration] = useState(60);
  const [maxStudents, setMaxStudents] = useState(6);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setDate(''); setTime('10:00'); setDuration(60); setMaxStudents(6); setNotes('');
    setError(null);
  }

  async function submit() {
    if (!date || !time) { setError('Indica fecha y hora'); return; }
    setSubmitting(true);
    setError(null);
    try {
      await createGroupBooking({
        teacher_id: teacherId,
        scheduled_at: `${date}T${time}:00`,
        max_students: maxStudents,
        duration_min: duration,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      reset();
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error al crear');
    } finally {
      setSubmitting(false);
    }
  }

  const today = new Date().toISOString().slice(0, 10);

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.drawer} aria-describedby={undefined}>
          <header className={styles.header}>
            <Dialog.Title className={styles.title}>Programar clase grupal</Dialog.Title>
            <Dialog.Close asChild>
              <button className={styles.closeBtn} aria-label="Cerrar">
                <Icon name="x" size={20} />
              </button>
            </Dialog.Close>
          </header>

          <div className={styles.body}>
            <p className={styles.lead}>
              Crea una clase grupal vacía. Los alumnos se apuntarán desde su pantalla de reservas.
            </p>

            <div className={styles.row}>
              <label className={styles.field}>
                <span>Fecha</span>
                <input type="date" min={today} value={date} onChange={(e) => setDate(e.target.value)} />
              </label>
              <label className={styles.field}>
                <span>Hora</span>
                <input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
              </label>
            </div>

            <div className={styles.row}>
              <label className={styles.field}>
                <span>Duración (min)</span>
                <input type="number" min={15} max={240} step={15} value={duration} onChange={(e) => setDuration(Number(e.target.value))} />
              </label>
              <label className={styles.field}>
                <span>Cupo máximo (1-6)</span>
                <input type="number" min={1} max={6} value={maxStudents} onChange={(e) => setMaxStudents(Number(e.target.value))} />
              </label>
            </div>

            <label className={styles.field}>
              <span>Notas (opcional)</span>
              <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </label>

            {error && <p className={styles.error}>{error}</p>}

            <footer className={styles.footer}>
              <Button variant="primary" disabled={submitting || !date} onClick={() => void submit()}>
                {submitting ? 'Creando…' : 'Crear clase grupal'}
              </Button>
            </footer>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
